/**
 * Single entry point for sending a lead to the server.
 *
 * Forms no longer talk to the CRM directly: they post here, this posts to
 * the Netlify function at /api/lead, and the function is the only thing
 * holding the CRM credentials.
 */

const LEAD_ENDPOINT = "/api/lead"

export const GENERIC_ERROR =
  "We couldn't submit your request. Please try again."

export const VALIDATION_ERROR =
  "Please check the highlighted fields and try again."

// Every attribution parameter this site captures from the landing URL and
// forwards with a lead. UTM fields describe the campaign; the click IDs
// (gclid / gbraid / wbraid) identify the ad click for Google Ads. They are
// stored under these exact keys in sessionStorage so capture and read agree.
// gatsby-browser.js already writes gclid/gbraid/wbraid under the same keys on
// every route change, so a lead keeps its click ID across SPA navigation.
export const ATTRIBUTION_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "gbraid",
  "wbraid",
]

/**
 * Reads the attribution values this site persists in sessionStorage.
 *
 * Returns every key in ATTRIBUTION_KEYS (empty string when unset) plus a
 * derived lead_source. lead_source mirrors the original inline behaviour: it
 * is the captured utm_source, or "website" when no utm_source was ever seen.
 *
 * Each UTM field is read independently of utm_source, so a campaign that links
 * with utm_campaign/medium/content but no utm_source still attributes.
 */
export const getStoredAttribution = () => {
  const attribution = {}
  ATTRIBUTION_KEYS.forEach(key => {
    attribution[key] = ""
  })
  attribution.lead_source = "website"

  if (typeof window === "undefined") return attribution

  try {
    ATTRIBUTION_KEYS.forEach(key => {
      attribution[key] = sessionStorage.getItem(key) || ""
    })
    if (attribution.utm_source) {
      attribution.lead_source = attribution.utm_source
    }
  } catch (e) {
    // Private browsing can throw on sessionStorage access; attribution is
    // optional, so fall through with the defaults rather than blocking a lead.
  }

  return attribution
}

/**
 * Stores attribution parameters from the current URL.
 *
 * Writes each key independently and only when the URL actually carries a
 * value, so a later page that lacks a parameter never overwrites what an
 * earlier landing page stored (e.g. a stored utm_campaign survives a
 * navigation to a page with no query string). Mirrors the empty-safe write
 * gatsby-browser.js already does for the click IDs.
 */
export const captureAttribution = () => {
  if (typeof window === "undefined") return

  try {
    const searchParams = new URLSearchParams(window.location.search)
    ATTRIBUTION_KEYS.forEach(key => {
      const value = searchParams.get(key)
      // Only persist real values; never overwrite a stored value with
      // "", null or undefined.
      if (value) {
        sessionStorage.setItem(key, value)
      }
    })
  } catch (e) {
    // Non-fatal: attribution is best-effort.
  }
}

/**
 * The attribution block to spread into a lead payload.
 *
 * Every form builds its payload differently, so this returns the full set of
 * attribution fields (UTMs, click IDs and derived lead_source) in one object
 * for the forms to spread. Keeping the field list here means a new attribution
 * field is added in one place, not in five form components.
 */
export const getAttributionPayload = () => {
  const attribution = getStoredAttribution()
  const payload = { lead_source: attribution.lead_source }
  ATTRIBUTION_KEYS.forEach(key => {
    payload[key] = attribution[key]
  })
  return payload
}

/**
 * Pushes the existing GTM lead event.
 *
 * Event name and shape are unchanged from the original inline code because
 * live Google Ads conversions are keyed to them. Only the timing changed:
 * this now runs after the server confirms the CRM accepted the lead.
 */
export const pushLeadConversion = ({ email, phone }) => {
  if (typeof window === "undefined") return
  if (typeof window.dataLayer === "undefined") return

  window.dataLayer.push({
    event: "form_submit",
    formName: "website_forms",
    enhanced_conversion_data: {
      email: email,
      phone_number: phone,
    },
  })
}

/**
 * Posts a lead payload to the Netlify function.
 *
 * Resolves only when the server confirms the lead was accepted and the CRM
 * took it. Any other outcome throws, so callers never fire a conversion for
 * a lead that did not land.
 */
export const submitLead = async payload => {
  let response
  let result

  try {
    response = await fetch(LEAD_ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
    result = await response.json()
  } catch (e) {
    // Network failure or a non-JSON response; details stay out of the UI.
    throw new Error(GENERIC_ERROR)
  }

  if (!response.ok || !result || !result.accepted) {
    // A spam-blocked request returns { ok: true, accepted: false }, which
    // lands here too: the visitor sees a normal error and the bot learns
    // nothing about why it was rejected.
    const error = new Error(
      result && result.errors ? VALIDATION_ERROR : GENERIC_ERROR
    )
    error.fieldErrors = (result && result.errors) || {}
    throw error
  }

  return result
}
